import React, { useEffect, useRef, useState } from "react";
import configProvider from "config/configProvider";
import { Checkbox, FormControlLabel, FormGroup, Grid, Skeleton } from "@mui/material";
import "mapbox-gl/dist/mapbox-gl.css";
import { Map, MapRef, Marker, Projection } from "react-map-gl";
import MDBox from "components/MDBox";
import Pin from "./Pin";
import { useMapWebglRecovery } from "map/useMapWebglRecovery";

const SingleMarkerMap = ({ location, height = "47vh" }) => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps } = useMapWebglRecovery(mapRef);
  const [mapCamera, setMapCamera] = useState({
    longitude: -100,
    latitude: 40,
    zoom: 3.5,
  });
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [toggleSatellite, setToggleSatellite] = useState(false);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/standard");
  const [projection, setProjection] = useState<Projection>({ name: "mercator" });

  const handleLayerToggle = (e) => {
    let map = mapRef.current.getMap();

    if (mapRef.current.getStyle().name === "Mapbox Satellite Streets") {
      setMapStyle("mapbox://styles/mapbox/streets-v11");
    } else {
      setMapStyle("mapbox://styles/mapbox/satellite-streets-v12");
    }

    setToggleSatellite(e.target.checked);
  };

  useEffect(() => {
    if (!location) return;

    setMapCamera({ zoom: 10, longitude: location.longitude, latitude: location.latitude });
    setMapLoaded(true);
    setLoading(false);

    return () => {
      setMapLoaded(false);
      mapRef.current = null;
    };
  }, [location]);

  const skeleton = () => {
    return (
      <>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={height} />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={11}>
            {" "}
            <Skeleton variant="rectangular" height={"74vh"} />
          </Grid>
        </Grid>
      </>
    );
  };

  const mapView = () => {
    return (
      <>
        <div>
          <MDBox>
            <FormGroup sx={{alignItems:'end'}}>
              <FormControlLabel
                label="Satellite View"
                control={<Checkbox id="satellite" checked={toggleSatellite} onChange={handleLayerToggle} />}
              />
            </FormGroup>
          </MDBox>
          <Map
            key={mapKey}
            reuseMaps={reuseMaps}
            {...mapCamera}
            onMove={(e) => setMapCamera(e.viewState)}
            // styleDiffing off: react-map-gl defaults to diffing one style into the next, and
            // the streets -> satellite-streets hop cannot be diffed -- mapbox logs "Unable to
            // perform style diff: Unimplemented: setSprite. Rebuilding the style from scratch."
            // and the rebuild leaves the map with a style but no live sources, so it stops
            // requesting tiles and paints the style's background colour. That background is
            // near-black, which is the black satellite map users report. Reload cleanly instead.
            styleDiffing={false}
            mapStyle={mapStyle}
            style={{ width: "100%", height: height }}
            mapboxAccessToken={configProvider.MapboxApiKey}
            ref={mapRef}
            projection={projection}
          >
            {mapLoaded && location && (
              <Marker latitude={location.latitude} longitude={location.longitude} anchor="bottom">
                <Pin />
              </Marker>
            )}
          </Map>
        </div>
      </>
    );
  };

  return loading ? skeleton() : mapView();
};

export default SingleMarkerMap;
